import {
  toast as rToast,
  type ToastContent,
  type ToastOptions,
  type Id,
} from "react-toastify";

/** Durations (ms) — change here to update UX app-wide. */
export const TOAST_DURATION = {
  success: 5000,
  info: 5000,
  warning: 8000,
  error: 8000,
} as const;

const baseOptions: ToastOptions = {
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const typeDefaults: Record<
  "success" | "info" | "warning" | "error",
  ToastOptions
> = {
  success: {
    ...baseOptions,
    autoClose: TOAST_DURATION.success,
    closeButton: true,
    className: "app-toast app-toast--success",
  },
  info: {
    ...baseOptions,
    autoClose: TOAST_DURATION.info,
    closeButton: true,
    className: "app-toast app-toast--info",
  },
  warning: {
    ...baseOptions,
    autoClose: TOAST_DURATION.warning,
    closeButton: true,
    className: "app-toast app-toast--warning",
  },
  error: {
    ...baseOptions,
    autoClose: TOAST_DURATION.error,
    closeButton: true,
    className: "app-toast app-toast--error",
  },
};

function mergeOptions(
  type: keyof typeof typeDefaults,
  options?: ToastOptions
): ToastOptions {
  const defaults = typeDefaults[type];
  if (!options) return { ...defaults };

  const { className, ...rest } = options;
  return {
    ...defaults,
    ...rest,
    className: [defaults.className, className].filter(Boolean).join(" "),
  };
}

function success(content: ToastContent, options?: ToastOptions): Id {
  return rToast.success(content, mergeOptions("success", options));
}

function error(content: ToastContent, options?: ToastOptions): Id {
  return rToast.error(content, mergeOptions("error", options));
}

function warning(content: ToastContent, options?: ToastOptions): Id {
  return rToast.warning(content, mergeOptions("warning", options));
}

function info(content: ToastContent, options?: ToastOptions): Id {
  return rToast.info(content, mergeOptions("info", options));
}

/** App-wide toast API — use this instead of importing from react-toastify. */
export const appToast = {
  success,
  error,
  warning,
  info,
  dismiss: rToast.dismiss,
  isActive: rToast.isActive,
  update: rToast.update,
};

/** Drop-in replacement for `import { toast } from "react-toastify"`. */
export const toast = appToast;
